import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, ArrowRight, ShieldCheck, MailCheck } from "lucide-react";

import Logo from "src/components/common/Logo";
import Input from "src/components/ui/Input";
import Button from "src/components/ui/Button";
import AuthThemeToggle from "src/components/auth/AuthThemeToggle";

import * as authTenantActions from "src/redux/slices/authTenantSlice";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";

const ForgotPasswordPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { forgotPassword } = useAppSelector((state) => state.authTenant);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    // Backend always responds the same way whether or not the email
    // matched (no enumeration) — so success here always means "submitted",
    // not "account found".
    if (forgotPassword?.data) {
      setSubmitted(true);
    }
  }, [forgotPassword?.data]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(authTenantActions.forgotPasswordRequest({ email }));
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6 dark:bg-gray-900">
      <AuthThemeToggle />
      <div className="w-full max-w-[380px]">
        <div className="rounded-[18px] border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-5 flex items-center justify-center gap-2">
            <Logo size="small" variant="contained" />
            <Logo size="large" variant="text" />
          </div>

          {submitted ? (
            <>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                <MailCheck size={24} />
              </div>
              <h2 className="mb-1.5 text-xl font-extrabold text-gray-900 dark:text-gray-100">
                Check your email
              </h2>
              <p className="mb-6 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                If an account exists for <strong>{email}</strong>, we've sent
                a link to reset your password.
              </p>
            </>
          ) : (
            <>
              <h2 className="mb-1.5 text-xl font-extrabold text-gray-900 dark:text-gray-100">
                Forgot password?
              </h2>
              <p className="mb-6 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                No worries — enter your work email and we'll send you a reset
                link.
              </p>

              <form className="space-y-3.5 text-left" onSubmit={handleSubmit}>
                <Input
                  label="Email or username"
                  type="email"
                  required
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  startAdornment={<Mail size={16} />}
                />
                <Button
                  type="submit"
                  fullWidth
                  isLoading={forgotPassword?.loading}
                  className="mt-1.5"
                >
                  Send reset link
                  <ArrowRight size={15} className="ml-1.5" />
                </Button>
              </form>
            </>
          )}

          <p className="mt-4.5 text-sm">
            <Link
              to="/app/login"
              className="inline-flex items-center gap-1.5 font-semibold text-primary-600 hover:underline dark:text-primary-400"
            >
              <ArrowLeft size={13} />
              Back to sign in
            </Link>
          </p>

          <div className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-gray-400 dark:text-gray-500">
            <ShieldCheck size={12} />
            Reset links expire after 30 minutes
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
